import React, { useEffect, useState } from 'react';
import MedicalDoc from '../../MedicalDocs/MedicalDocDialog';
import { Icon } from '@ohif/ui';
import { Table } from '../../../shared/custom-ui';
import { V2Button } from '../../../shared/custom-ui/v2-components';
import TableSectionSkeleton from '../../../shared/custom-ui/skeleton/TableSectionSkeleton/TableSectionSkeleton';
import ApiServices from '../../../shared/ApiServices/ApiServices';
import { columns as baseColumns } from './documentColumns.constant';
import UnifiedPagination from '../../../shared/custom-ui/UnifiedPagination';

export default function Documents({ Patientid }) {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [docDialogOpen, setDocDialogOpen] = useState(false);
    const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [documentsPerPage] = useState(5);

    useEffect(() => {
        if (Patientid) fetchMedicalDocs();
    }, [Patientid]);

    const fetchMedicalDocs = async () => {
        setLoading(true);
        try {
            const response = await ApiServices.getMedicalDocs(Patientid);
            const { medicalDocs } = response;
            setDocuments(medicalDocs?.length > 0 ? medicalDocs : []);
        } catch {
            setDocuments([]);
        } finally {
            setLoading(false);
        }
    };

    const handleUploadSuccess = () => fetchMedicalDocs();

    const openDocsDialog = (patientId: string) => {
        setSelectedPatientId(patientId);
        setDocDialogOpen(true);
    };

    const closeDocsDialog = () => {
        setSelectedPatientId(null);
        setDocDialogOpen(false);
    };

    const indexOfLastDocument = currentPage * documentsPerPage;
    const indexOfFirstDocument = indexOfLastDocument - documentsPerPage;
    const currentDocuments = documents.slice(indexOfFirstDocument, indexOfLastDocument);
    const totalPages = Math.ceil(documents.length / documentsPerPage);

    const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

    // ✅ Fix alignment by wrapping link in a flex container
    const actionColumn = {
        ...baseColumns.find(col => col.isAction),
        formatter: (_: any, doc: string) => (
            <div className="flex items-center h-full">
                <a
                    href={doc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 font-medium underline hover:text-blue-800 transition-colors"
                >
                    View Document
                </a>
            </div>
        ),
    };

    const customColumns = baseColumns.map(col =>
        col.key === 'index'
            ? {
                ...col,
                formatter: (_: any, __: any, rowIndex: number) => indexOfFirstDocument + rowIndex + 1,
            }
            : col.isAction
                ? actionColumn
                : col
    );

    if (loading) {
        return <TableSectionSkeleton rows={5} columns={4} showActionButton />;
    }

    return (
        <section className="flex h-full flex-col rounded-xl bg-gradient-to-b from-blue-50 to-gray-50 shadow-lg">
            {/* Header */}
            <div className="sticky top-0 z-10 border-b px-6 py-4 rounded-t-[var(--radius-lg)] flex items-center justify-between" style={{ background: 'var(--primary-25)', borderColor: 'var(--border-subtle)' }}>
                <h2 className="text-lg font-bold">Documents</h2>
                <V2Button
                    variant="primary"
                    size="small"
                    onClick={() => openDocsDialog(Patientid)}
                    leftIcon={<Icon name="add-plus-icon" className="h-5 w-5 text-white" />}
                >
                    <span className="text-sm font-medium">Upload Document</span>
                </V2Button>
            </div>

            {/* Table Container */}
            <div
                className="relative flex-1 px-6 py-4 overflow-y-auto max-h-[calc(100vh-464px)]"
                style={{ background: 'var(--bg-card)' }}
            >
                <Table
                    columns={customColumns}
                    data={currentDocuments}
                    rowKey={(_, index) => index}
                    stickyHeader={false}
                    emptyText="No documents available."
                />

                {/* Pagination */}
                {documents.length > 0 && (
                    <div className="pt-6">
                        <UnifiedPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={paginate}
                        />
                    </div>
                )}
            </div>
            <MedicalDoc
                isOpen={docDialogOpen}
                patientId={selectedPatientId}
                onClose={closeDocsDialog}
                onUploadSuccess={handleUploadSuccess}
            />
        </section>
    );
}
